import { useRef, useEffect, useCallback, useState } from 'react';
import { useEditorStore } from '../../store/editorStore';
import { usePlayheadStore } from '../../store/playheadStore';
import { useLoopStore } from '../../store/loopStore';
import { useMobileTimelineInteraction } from '../../hooks/useMobileTimelineInteraction';
import { generateThumbnails, getThumbnails } from '../../engine/thumbnailGenerator';
import { getWaveform, getAudioBufferData } from '../../engine/proxyManager';
import { generatePeaksAtZoom, type PeakData } from '../../engine/peakGenerator';
import {
  drawRuler,
  drawTrackLanes,
  drawClipBlocks,
  drawThumbnails,
  drawWaveform,
  drawPlayhead,
  drawTrimHandles,
  drawFadeOverlay,
  drawSelectionRegion,
  drawEffectClips,
  setDrawingProject,
  getTotalTracksHeight,
  getTrackY,
  getTrackHeight,
  type DrawContext,
} from '../../lib/canvasDrawing';
import { msToPixel } from '../../lib/timeUtils';

/** Fat mobile trim handles drawn on top of the desktop ones */
const MOBILE_TRIM_HANDLE_WIDTH = 16;
const TRIM_HANDLE_COLOR = '#07E5B0';

function drawMobileTrimHandles(
  dc: DrawContext,
  tracks: import('../../types').Track[],
  selectedClipId: string | null,
) {
  if (!selectedClipId) return;
  const { ctx, pxPerMs, scrollOffsetMs } = dc;

  for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
    const track = tracks[trackIndex];
    const clip = track.clips.find((c) => c.id === selectedClipId);
    const effectClip = track.effectClips?.find((ec) => ec.id === selectedClipId);
    const foundClip = clip || effectClip;
    if (!foundClip) continue;

    const trackY2 = getTrackY(tracks, trackIndex);
    const trackH = getTrackHeight(track);
    const clipX = Math.max(0, msToPixel(foundClip.startMs, pxPerMs, scrollOffsetMs));
    const clipXEnd = msToPixel(foundClip.startMs + foundClip.durationMs, pxPerMs, scrollOffsetMs);
    const clipY = trackY2 + 2;
    const clipH = trackH - 4;

    if (clipXEnd - clipX <= MOBILE_TRIM_HANDLE_WIDTH * 2) break;

    // Left handle — rounded left side
    ctx.fillStyle = TRIM_HANDLE_COLOR;
    ctx.globalAlpha = 0.9;
    ctx.beginPath();
    const r = 6;
    ctx.moveTo(clipX + r, clipY);
    ctx.lineTo(clipX + MOBILE_TRIM_HANDLE_WIDTH, clipY);
    ctx.lineTo(clipX + MOBILE_TRIM_HANDLE_WIDTH, clipY + clipH);
    ctx.lineTo(clipX + r, clipY + clipH);
    ctx.arcTo(clipX, clipY + clipH, clipX, clipY + clipH - r, r);
    ctx.lineTo(clipX, clipY + r);
    ctx.arcTo(clipX, clipY, clipX + r, clipY, r);
    ctx.closePath();
    ctx.fill();

    // Left grip lines
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.lineWidth = 1;
    const cx1 = clipX + MOBILE_TRIM_HANDLE_WIDTH / 2;
    for (let i = -6; i <= 6; i += 4) {
      ctx.beginPath();
      ctx.moveTo(cx1, clipY + clipH / 2 + i);
      ctx.lineTo(cx1 + 3, clipY + clipH / 2 + i);
      ctx.stroke();
    }

    // Right handle — rounded right side
    ctx.fillStyle = TRIM_HANDLE_COLOR;
    ctx.beginPath();
    const rx = clipXEnd - MOBILE_TRIM_HANDLE_WIDTH;
    ctx.moveTo(rx, clipY);
    ctx.lineTo(clipXEnd - r, clipY);
    ctx.arcTo(clipXEnd, clipY, clipXEnd, clipY + r, r);
    ctx.lineTo(clipXEnd, clipY + clipH - r);
    ctx.arcTo(clipXEnd, clipY + clipH, clipXEnd - r, clipY + clipH, r);
    ctx.lineTo(rx, clipY + clipH);
    ctx.closePath();
    ctx.fill();

    // Right grip lines
    const cx2 = clipXEnd - MOBILE_TRIM_HANDLE_WIDTH / 2;
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    for (let i = -6; i <= 6; i += 4) {
      ctx.beginPath();
      ctx.moveTo(cx2 - 3, clipY + clipH / 2 + i);
      ctx.lineTo(cx2, clipY + clipH / 2 + i);
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
    break;
  }
}

export function MobileTimelineCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef(0);
  // Hook attaches touch listeners directly with { passive: false }
  useMobileTimelineInteraction(canvasRef);

  const loopStart = useLoopStore((s) => s.selectionStart);
  const loopEnd = useLoopStore((s) => s.selectionEnd);
  const loopEnabled = useLoopStore((s) => s.loopEnabled);
  const project = useEditorStore((s) => s.project);
  const tracks = project.tracks;
  const durationMs = project.durationMs;
  const timeline = useEditorStore((s) => s.timeline);

  const assetIdKey = tracks.flatMap((t) => t.clips.map((c) => c.assetId)).join(',');

  const [thumbsMap, setThumbsMap] = useState<Map<string, ImageBitmap[]>>(new Map());
  const [peaksMap, setPeaksMap] = useState<Map<string, PeakData | Float32Array>>(new Map());
  const audioBufferCache = useRef<Map<string, AudioBuffer>>(new Map());
  const zoomDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastZoomPxPerMs = useRef(timeline.pxPerMs);

  // Load thumbnails & waveforms (same logic as desktop TimelineCanvas)
  useEffect(() => {
    const assetIds = new Set<string>();
    for (const track of tracks) {
      for (const clip of track.clips) assetIds.add(clip.assetId);
    }

    let cancelled = false;

    for (const assetId of assetIds) {
      const cached = getThumbnails(assetId);
      if (cached) {
        setThumbsMap((prev) => {
          if (prev.get(assetId) === cached) return prev;
          const next = new Map(prev);
          next.set(assetId, cached);
          return next;
        });
      } else {
        generateThumbnails(assetId).then((result) => {
          if (cancelled) return;
          setThumbsMap((prev) => {
            const next = new Map(prev);
            next.set(assetId, result);
            return next;
          });
        });
      }

      getWaveform(assetId).then((data) => {
        if (cancelled || !data) return;
        setPeaksMap((prev) => {
          if (prev.get(assetId) === data) return prev;
          const next = new Map(prev);
          next.set(assetId, data);
          return next;
        });
      });

      if (!audioBufferCache.current.has(assetId)) {
        getAudioBufferData(assetId).then(async (raw) => {
          if (cancelled || !raw) return;
          const ctx = new AudioContext();
          const buf = await ctx.decodeAudioData(raw.slice(0));
          await ctx.close();
          audioBufferCache.current.set(assetId, buf);
        });
      }
    }

    return () => { cancelled = true; };
  }, [assetIdKey, tracks]);

  // Re-generate peaks on zoom change (debounced)
  useEffect(() => {
    if (timeline.pxPerMs === lastZoomPxPerMs.current) return;
    lastZoomPxPerMs.current = timeline.pxPerMs;

    if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    zoomDebounceRef.current = setTimeout(() => {
      const buffers = audioBufferCache.current;
      if (buffers.size === 0) return;
      const newPeaks = new Map(peaksMap);
      let changed = false;
      for (const [assetId, buf] of buffers) {
        const pd = generatePeaksAtZoom(buf, timeline.pxPerMs);
        newPeaks.set(assetId, pd);
        changed = true;
      }
      if (changed) setPeaksMap(newPeaks);
    }, 200);

    return () => {
      if (zoomDebounceRef.current) clearTimeout(zoomDebounceRef.current);
    };
  }, [timeline.pxPerMs, peaksMap]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);

    setDrawingProject(project);

    const dc: DrawContext = {
      ctx,
      width: rect.width,
      height: rect.height,
      pxPerMs: timeline.pxPerMs,
      scrollOffsetMs: timeline.scrollOffsetMs,
      durationMs,
    };

    ctx.clearRect(0, 0, rect.width, rect.height);

    drawRuler(dc);
    drawTrackLanes(dc, tracks);
    drawClipBlocks(dc, tracks, timeline.selectedClipId);

    tracks.forEach((track, trackIndex) => {
      if (track.type === 'effect') return;
      for (const clip of track.clips) {
        if (track.type === 'video') {
          const thumbs = thumbsMap.get(clip.assetId);
          if (thumbs) drawThumbnails(dc, thumbs, clip, trackIndex, 500, tracks);
        } else {
          const peaks = peaksMap.get(clip.assetId);
          if (peaks) drawWaveform(dc, peaks, clip, trackIndex, tracks);
        }
      }
    });

    tracks.forEach((track, trackIndex) => {
      if (track.type === 'effect') return;
      for (const clip of track.clips) {
        if (clip.fadeIn || clip.fadeOut) drawFadeOverlay(dc, clip, trackIndex, tracks);
      }
    });

    const currentTimeMs = usePlayheadStore.getState().currentTimeMs;
    drawEffectClips(dc, tracks, timeline.selectedClipId, currentTimeMs);

    // Draw fat mobile trim handles instead of desktop ones
    drawMobileTrimHandles(dc, tracks, timeline.selectedClipId);

    if (loopStart !== null && loopEnd !== null) {
      drawSelectionRegion(dc, loopStart, loopEnd, loopEnabled);
    }

    drawPlayhead(dc, currentTimeMs);
  }, [project, tracks, durationMs, timeline, thumbsMap, peaksMap, loopStart, loopEnd, loopEnabled]);

  useEffect(() => {
    function tick() {
      draw();
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [draw]);

  // Use the same height calculation as drawing functions (56px tracks)
  const totalHeight = getTotalTracksHeight(tracks);

  return (
    <div className="relative w-full" style={{ minHeight: totalHeight }}>
      <canvas
        ref={canvasRef}
        className="w-full touch-none"
        style={{ display: 'block', height: totalHeight }}
      />
    </div>
  );
}
